import { createApi } from '@reduxjs/toolkit/query/react';

import { RobotShape, RobotsResponseShape } from 'models/robots';
import { baseQuery } from '../..';
import { LinksMap } from 'models/api';
import { DirectionType } from 'components/Dashboard/Operator/RobotView/Info/Schedule/ScheduleModal/ScheduleModal';
import getConfig from '_config';

type UpdateScheduleProps = {
  schedule: string;
  url: string;
  direction: DirectionType;
};

type UpdateAutoScheduleProps = {
  autoschedule: boolean;
  url: string;
};

type UpdateModeProps = {
  operation_mode: number;
  url: string;
};

export type OperationModeOption = {
  mode_id: number;
  name: string;
};

type OperationModeOptionsResponse = {
  collection: OperationModeOption[];
  links: LinksMap;
};

const getUUID = (url) => {
  const uuidPattern = /([a-f0-9\-]{36})/g;
  const uuids = url.match(uuidPattern);

  return uuids ? uuids[uuids.length - 1] : null;
};

export const RobotsApi = createApi({
  reducerPath: 'RobotsApi',
  tagTypes: ['robots', 'robot', 'schedule', 'auto-schedule', 'robot-mode'],
  baseQuery,
  endpoints: (builder) => ({
    getTableData: builder.query<RobotsResponseShape, string>({
      query: (url) => ({
        url: url,
      }),
      providesTags: ['robots'],
    }),
    getRobot: builder.query<RobotShape, string>({
      query: (url) => ({
        url: url,
      }),
      providesTags: ['robot'],
    }),
    getSchedule: builder.query<{ schedule: string; links: LinksMap }, string>({
      query: (url) => ({
        url: url,
      }),
      providesTags: ['schedule'],
    }),
    getScheduleList: builder.query<
      { schedules: string[]; links: LinksMap },
      string
    >({
      query: (url) => ({
        url: url,
      }),
    }),
    getAutoSchedule: builder.query<
      { autoschedule: string; links: LinksMap },
      string
    >({
      query: (url) => ({
        url: url,
      }),
      providesTags: (_result, _error, url) => [
        { type: 'auto-schedule', id: getUUID(url) },
      ],
    }),
    updateAutoSchedule: builder.mutation<void, UpdateAutoScheduleProps>({
      query: ({ url, ...otherBodyprops }) => ({
        url: url,
        method: 'POST',
        body: otherBodyprops,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'auto-schedule', id: getUUID(arg.url) },
      ],
    }),
    updateSchedule: builder.mutation<void, UpdateScheduleProps>({
      query: ({ url, ...otherBodyprops }) => ({
        url: url,
        method: 'POST',
        body: otherBodyprops,
      }),
      invalidatesTags: ['schedule'],
    }),
    getDepthOffset: builder.query<{ depth: number; links: LinksMap }, string>({
      query: (url) => ({
        url: url,
      }),
    }),
    getMode: builder.query<{ operation_mode: string; links: LinksMap }, string>(
      {
        query: (url) => ({
          url: url,
        }),
        providesTags: ['robot-mode'],
      },
    ),
    updateRobotMode: builder.mutation<void, UpdateModeProps>({
      query: ({ url, operation_mode }) => ({
        url: url,
        method: 'POST',
        body: {
          operation_mode,
        },
      }),
      invalidatesTags: ['robot-mode'],
    }),
    getOperationModes: builder.query<OperationModeOptionsResponse, void>({
      query: () => ({
        url: `${getConfig()?.backendUrl}operation_modes`,
      }),
    }),
  }),
});

export const {
  useGetTableDataQuery,
  useGetScheduleQuery,
  useGetScheduleListQuery,
  useGetRobotQuery,
  useUpdateScheduleMutation,
  useGetDepthOffsetQuery,
  useGetAutoScheduleQuery,
  useUpdateAutoScheduleMutation,
  useGetModeQuery,
  useUpdateRobotModeMutation,
  useGetOperationModesQuery,
} = RobotsApi;
